import React, { useState, useEffect,useRef } from "react";
import "./AddOffer.css";
import useOffer, { useAddOffer, useUpdateOffer } from "../../hooks/useOffer";
import productService from "../../api/services/productService";
import Cropper from "react-easy-crop";
import Select from "react-select";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useFileUploader from "../../hooks/useFileUploader";
import { useNavigate, useParams,useLocation } from "react-router-dom";
import vendorService from "../../api/services/vendorService";
import usePath from "../../hooks/usePath";
import SafeImage from "../../components/common/SafeImage";
import Swal from "sweetalert2";

// Custom hook for fetching products (robust to API response shape)
const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const res = await axios.get(usePath("/api/bulk/public/products"));
      // Handle both {data: [...]} and [...]
      if (Array.isArray(res.data)) return res.data;
      if (res.data && Array.isArray(res.data.data)) return res.data.data;
      return [];
    },
  });
};

// Vendor Selection Popup Component
const VendorSelectionPopup = ({
  isOpen,
  onClose,
  selectedProducts,
  availableProducts,
  combos,
  onConfirm,
  comboIndex,
  vendors,
  vendorProductOptions,
  onVendorChange,
  initialVendorId,
    isProductBlocked,
}) => {
  const [localSelected, setLocalSelected] = useState(selectedProducts);
  const [selectedVendorId, setSelectedVendorId] = useState(
    initialVendorId || ""
  );
  const [selectedVendorProducts, setSelectedVendorProducts] = useState([]);
  // Move productSearch hook here, before any return
  const [productSearch, setProductSearch] = useState("");

  const uniqueSelected = [...new Set(localSelected)];

  useEffect(() => {
    setLocalSelected(
    [...new Set((selectedProducts || []).map((id) => String(id)))]
  );
    setSelectedVendorId(initialVendorId || "");
  }, [selectedProducts, initialVendorId]);

  // Update selected vendor products when vendor changes
  useEffect(() => {
    if (selectedVendorId) {
      if (vendorProductOptions[selectedVendorId]) {
        setSelectedVendorProducts(vendorProductOptions[selectedVendorId]);
      } else {
        // Fetch products for this vendor if not already loaded
        onVendorChange(selectedVendorId);
        setSelectedVendorProducts([]);
      }
    } else {
      setSelectedVendorProducts([]);
    }
  }, [selectedVendorId, vendorProductOptions, onVendorChange]);

const handleProductToggle = (productId) => {
  const pid = String(productId);

  setLocalSelected((prev) => {
    if (prev.includes(pid)) {
      return prev.filter((id) => id !== pid);
    } else {
      return [...prev, pid];
    }
  });
};

  const handleConfirm = () => {
    if (!selectedVendorId) {
      toast.error("Please select a vendor first.");
      return;
    }

     const uniqueSelected = [...new Set(localSelected)]; // ✅ ADD THIS LINE HERE

    if (uniqueSelected.length >= 2) {
      onConfirm(comboIndex, uniqueSelected, selectedVendorId);
      onClose();
    } else {
      toast.error("Please select at least 2 products for this combo.");
    }
  };

  if (!isOpen) return null;

  // In VendorSelectionPopup, filter and sort products for search accuracy
  const filteredProducts = (() => {
    const lowerSearch = productSearch.trim().toLowerCase();
    if (!lowerSearch) return selectedVendorProducts;
    const startsWith = selectedVendorProducts
      .filter((product) =>
        product.product_name.toLowerCase().startsWith(lowerSearch)
      )
      .sort((a, b) => a.product_name.localeCompare(b.product_name));
    const includes = selectedVendorProducts
      .filter(
        (product) =>
          !product.product_name.toLowerCase().startsWith(lowerSearch) &&
          product.product_name.toLowerCase().includes(lowerSearch)
      )
      .sort((a, b) => a.product_name.localeCompare(b.product_name));
    return [...startsWith, ...includes];
  })();


  return (
    <div className="vendor-popup-overlay">
      <div className="vendor-popup">
        <div className="vendor-popup-header">
          <h3>
            Select Products for{" "}
            {selectedVendorId
              ? vendors.find((v) => v.vendor_id === selectedVendorId)?.user
                  ?.name
              : "Vendor"}
          </h3>
          <button onClick={onClose} className="vendor-popup-close">
            &times;
          </button>
        </div>
        <div className="vendor-popup-content">
          <div className="vendor-popup-vendor-selection">
            <label className="vendor-popup-vendor-label">Choose Vendor:</label>
            <Select
              className="vendor-popup-vendor-select"
              options={vendors
                .filter((ven) => {
                  return (
                    !combos.some((Cven) => Cven.vendor_id === ven.vendor_id) ||
                    String(ven.vendor_id) === String(selectedVendorId)
                  );
                }) //then already add combo for vendor
                .map((vendor) => ({
                  value: vendor.vendor_id,
                  label: vendor.user?.name || `Vendor #${vendor.vendor_id}`,
                }))}
              value={
                vendors
                  .filter(
                    (vendor) =>
                      String(vendor.vendor_id) === String(selectedVendorId)
                  )
                  .map((vendor) => ({
                    value: vendor.vendor_id,
                    label: vendor.user?.name || `Vendor #${vendor.vendor_id}`,
                  }))[0] || null
              }
              onChange={(option) =>
                setSelectedVendorId(option ? option.value : "")
              }
              placeholder="Select a vendor..."
              isClearable
              menuPortalTarget={
                typeof window !== "undefined" ? document.body : null
              }
              maxMenuHeight={600}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          {selectedVendorId && (
            <>
              <div className="vendor-popup-instruction">
                Select at least 2 products from{" "}
                <strong>
                  {
                    vendors.find((v) => v.vendor_id === selectedVendorId)?.user
                      ?.name
                  }
                </strong>
                :
              </div>
              <input
                type="text"
                className="vendor-popup-product-search"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                style={{ marginBottom: 8, width: "100%" }}
              />
                  {filteredProducts.length > 0 && (
                    <button
                      type="button"
                      className="select-all-btn"
                      style={{ marginBottom: 10 }}
                      onClick={() => {
                        const allIds = filteredProducts.map((p) => String(p.product_id));

                        const allSelected =
                          allIds.length > 0 && allIds.every((id) => localSelected.includes(id));

                        setLocalSelected(allSelected ? [] : allIds);
                      }}
                    >
                      {filteredProducts.every((p) =>
                        localSelected.includes(String(p.product_id))
                      )
                        ? "Unselect All"
                        : "Select All"}
                    </button>
                  )}
                  <div className="vendor-popup-products">
                  {filteredProducts.map((product) => (
                    <label
                      key={product.product_id}
                      className="vendor-popup-product-item"
                    >
                      <input
                  type="checkbox"
                  disabled={
                    isProductBlocked?.(product.product_id) &&
                    !localSelected.includes(String(product.product_id)) // ✅ already selected இருந்தா disable செய்யாத
                  }
                checked={localSelected.includes(String(product.product_id))}
                  onChange={() => handleProductToggle(product.product_id)}
                />
                      <span className="vendor-popup-product-name">
                        {product.product_name}
                      </span>
                    </label>
                  ))}
                </div>
              <div className="vendor-popup-selected-count">
                {/* Selected: {localSelected.length} products */}
                Selected: {new Set(localSelected).size} products
                {localSelected.length < 2 && (
                  <span className="vendor-popup-warning">
                    {" "}
                    (Need at least 2)
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <div className="vendor-popup-actions">
          <button
            onClick={onClose}
            className="vendor-popup-btn vendor-popup-btn-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="vendor-popup-btn vendor-popup-btn-confirm"
            disabled={localSelected.length < 2}
          >
            Save Products
          </button>
        </div>
      </div>
    </div>
  );
};

const AddOffer = () => {
  const uniqueInSameOrder = (arr) => {
  const seen = new Set();
  return (arr || []).filter((x) => {
    const k = String(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: products = [],
    isLoading: productsLoading,
    error: productsError,
  } = useProducts();
  const location = useLocation();
  const focusProductId = location?.state?.focusProductId
    ? String(location.state.focusProductId)
    : "";
  const didAutoFocusRef = useRef(false);
useEffect(() => {
  didAutoFocusRef.current = false;
}, [id]);

  const [form, setForm] = useState({
    name: "",
    offer_type: "single", // renamed for clarity
    discount_type: "percentage", // renamed for clarity
    value: "",
    start_date: new Date().toISOString().split("T")[0], // Auto-select today's date
    end_date: "",
    is_active: true,
    vendor_id: "", // add vendor_id to form state
  });

  const [image, setImage] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Replace products state and useEffect with React Query
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [vendors, setVendors] = useState([]); // Add vendors state
  const [vendorProducts, setVendorProducts] = useState([]); // For vendor_products
  const [vendorProductOptions, setVendorProductOptions] = useState({}); // { vendor_id: [products] }
  const [combos, setCombos] = useState([{ products: [], vendor_id: "" }]);
  const [selectedSingleProducts, setSelectedSingleProducts] = useState([]);
  const [selectedSingleVendors, setSelectedSingleVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);

  // Combo popup state
  const [comboPopup, setComboPopup] = useState({
    isOpen: false,
    comboIndex: 0,
    selectedProducts: [],
    vendor_id: "",
  });

  const [singleOfferList, setSingleOfferList] = useState([]); 
  const [currentProduct, setCurrentProduct] = useState("");
  const [currentVendors, setCurrentVendors] = useState([]);
  const [editProductId, setEditProductId] = useState(null);

  // Warning message state
  const [showDateWarning, setShowDateWarning] = useState(false);

const handleAddSingleOfferItem = async () => {
  if (!currentProduct || currentVendors.length === 0) return;

  //NEW offer 
  if (!id) {
    for (const vid of currentVendors) {
      const existing = findActiveSingleOfferForProductVendor(currentProduct, vid);
      if (existing) {
        await showExistingOfferPopup(existing, currentProduct);
        return;
      }
    }
  } else {
    
    const blockedOffer = currentVendors
  .map((vid) =>
    findActiveSingleOfferForProductVendor(currentProduct, vid)
  )
  .find((offer) => offer);
      if (blockedOffer) {
        debugger;
        toast.error(`This Product+Vendor already has an active offer in another offer: "${blockedOffer.name}"`);
        return;
      }
  }

  // save to list
  setSingleOfferList((prev) => {
    const pid = String(currentProduct);
    const newVendors = uniqueInSameOrder(currentVendors.map(String));
    const next = prev.filter((x) => String(x.product_id) !== pid);
    next.push({ product_id: pid, vendor_ids: newVendors });
    return next;
  });
  setCurrentProduct("");
  setCurrentVendors([]);
};

  const handleRemoveSingleOfferItem = (product_id) => {
    setSingleOfferList((prev) =>
      prev.filter((item) => item.product_id !== product_id)
    );
  };

  const handleEditSingleOfferItem = (product_id) => {
    const item = singleOfferList.find(
      (i) => String(i.product_id) === String(product_id)
    );
    setEditProductId(String(product_id));    setCurrentProduct(String(item.product_id));
    setCurrentVendors((item.vendor_ids || []).map((v) => String(v)));
    setProductInput("");
    setVendorSearch("");
  };

const handleSaveEditSingleOfferItem = async () => {
  if (!currentProduct || currentVendors.length === 0) return;

  const blockedOffer = currentVendors
    .map((vid) => findActiveSingleOfferForProductVendor(currentProduct, vid))
    .find((o) => o); // first matched offer object

  if (blockedOffer) {
    toast.error(
      `This Product+Vendor already exists in active offer: "${blockedOffer?.name || "Offer"}"`
    );
    return;
  }

  setSingleOfferList((prev) => {
    const pid = String(currentProduct);
    const newVendors = uniqueInSameOrder(currentVendors.map(String));
    const next = prev.filter((x) => String(x.product_id) !== String(editProductId));
    next.push({ product_id: pid, vendor_ids: newVendors });
    return next;
  });

  setEditProductId(null);
  setCurrentProduct("");
  setCurrentVendors([]);
};

  const handleCancelEditSingleOfferItem = () => {
    setEditProductId(null);
    setCurrentProduct("");
    setCurrentVendors([]);
  };

  useEffect(() => {
    vendorService
      .getAllVendors()
      .then((res) => {
        if (res.success) {
          setVendors(Array.isArray(res.data) ? res.data : []);
        } else {
          setVendors([]);
        }
      })
      .catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    if (id) {
      SetIsLoading(true);
      axios
        .get(
          `${
            import.meta.env.VITE_API_URL || "http://localhost:8000"
          }/api/offers/${id}`
        )
        .then((res) => {
          const data = res.data.data || res.data;
          console.log("Edit offer data:", data);

          setForm((prev) => ({
            
            ...prev,
            name: data.name || "",
            offer_type: data.offer_type || "single",
            discount_type: data.discount_type  === "percentage" ? "percentage" : "percentage",  // default to percentage if invalid
            value: data.value || "",
            start_date:
              data.start_date || new Date().toISOString().split("T")[0],
            end_date: data.end_date || "",
            is_active: data.is_active !== undefined ? data.is_active : true,
            image_path: data.image_path || "",
          }));

          // Set image preview
          if (data.image_path) {
            setImagePreview(
              data.image_path.startsWith("http")
                ? data.image_path
                : `${
                    import.meta.env.VITE_API_URL || "http://localhost:8000"
                  }/offers/${data.image_path}`
            );
          }

          // Prefill singleOfferList for single offers (group by product_id and aggregate vendor_ids)
          if (
            data.offer_type === "single" &&
            Array.isArray(data.single_product_offers)
          ) {
            const groupedByProduct = {};
            data.single_product_offers.forEach((item) => {
              const pid = String(item.product_id);
              if (!groupedByProduct[pid]) {
                groupedByProduct[pid] = {
                  product_id: item.product_id,
                  vendor_ids: [],
                };
              }
              if (!groupedByProduct[pid].vendor_ids.includes(item.vendor_id)) {
                groupedByProduct[pid].vendor_ids.push(item.vendor_id);
              }
            });
            const singleOffers = Object.values(groupedByProduct);
            console.log("Setting single offers (grouped):", singleOffers);
            setSingleOfferList(singleOffers);
          }

          // Prefill combos for combo offers
          if (data.offer_type === "combo" && Array.isArray(data.combo_offers)) {
            const combosByVendor = {};
            data.combo_offers.forEach((item) => {
              //select 1
              let vendor_product = Array.isArray(item?.vendor_product)
                ? item?.vendor_product[0]
                : item?.vendor_product;
              if (!combosByVendor[vendor_product.vendor_id]) {
                combosByVendor[vendor_product.vendor_id] = [];
              }
              combosByVendor[vendor_product.vendor_id].push(
                vendor_product.product_id
              );
            });

            const comboList = Object.entries(combosByVendor).map(
              ([vendor_id, products]) => ({
                vendor_id: parseInt(vendor_id),
                // products: products,
                products: [...new Set(products)], // UNIQUE product_ids
              })
            );

            //             {
            //     "vendor_id": 39,
            //     "products": [
            //         57,
            //         57,
            //         57,
            //         56
            //     ]
            // }
            console.log("Setting combos:", comboList);
            setCombos(comboList);

            // Prefetch vendor product lists for all vendors present in combos
            comboList.forEach((c) => {
              if (c.vendor_id) {
                fetchVendorProducts(c.vendor_id);
              }
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching offer for edit:", error);
          toast.error("Failed to load offer data");
        })
        .finally(() => SetIsLoading(false));
    }
  }, [id]);




useEffect(() => {
  if (!id) return;
  if (!focusProductId) return;
  if (didAutoFocusRef.current) return;
  if (!singleOfferList || singleOfferList.length === 0) return;

  const exists = singleOfferList.some(
    (x) => String(x.product_id) === String(focusProductId)
  );

  if (exists) {
    didAutoFocusRef.current = true;
    handleEditSingleOfferItem(focusProductId);
  }
}, [id, focusProductId, singleOfferList]);
  // In useEffect, fetch products for the selected vendor (only for single offers)
  useEffect(() => {
    if (form.offer_type === "single" && form.vendor_id) {
      productService.getProductsBulkByVendorId(form.vendor_id).then((res) => {
        if (res.success) {
          setVendorProductOptions({ [form.vendor_id]: res.data });
        } else {
          setVendorProductOptions({ [form.vendor_id]: [] });
        }
      });
    } else if (form.offer_type === "single") {
      setVendorProductOptions({});
    }
  }, [form.vendor_id, form.offer_type]);

  // Update handleVendorProductCheckbox to use form.vendor_id
  const handleVendorProductCheckbox = (vendorId, productId) => {
    setVendorProducts((prev) => {
      const exists = prev.some(
        (vp) => vp.vendor_id === vendorId && vp.product_id === productId
      );
      if (exists) {
        return prev.filter(
          (vp) => !(vp.vendor_id === vendorId && vp.product_id === productId)
        );
      } else {
        return [...prev, { vendor_id: vendorId, product_id: productId }];
      }
    });
  };

  // Combo builder handlers
  const addCombo = () =>
    setCombos([...combos, { products: [], vendor_id: "" }]);
  const removeCombo = (idx) => setCombos(combos.filter((_, i) => i !== idx));

  // Open combo popup for editing
  const openComboPopup = (comboIdx) => {
    const combo = combos[comboIdx];
    setComboPopup({
      isOpen: true,
      comboIndex: comboIdx,
      selectedProducts: combo?.products || [],
      vendor_id: combo?.vendor_id || "",
    });
  };

  // Handle combo confirmation from popup
  const handleComboConfirm = (comboIdx, selectedProducts, vendorId) => {
    setCombos((prev) =>
      prev.map((combo, i) => {
        if (i !== comboIdx) return combo;
        return { ...combo, products: selectedProducts, vendor_id: vendorId };
      })
    );
  };

  // Close combo popup
  const closeComboPopup = () => {
    setComboPopup({
      isOpen: false,
      comboIndex: 0,
      selectedProducts: [],
      vendor_id: "",
    });
  };

  // Fetch products for a specific vendor (for combo mode)
  const fetchVendorProducts = async (vendorId) => {
    if (!vendorProductOptions[vendorId]) {
      try {
        const res = await productService.getProductsBulkByVendorId(vendorId);
        if (res.success) {
          setVendorProductOptions((prev) => ({
            ...prev,
            [vendorId]: Object.values(
              res.data.reduce((acc, item) => {
                if (!acc[item.product_id]) acc[item.product_id] = item;
                return acc;
              }, {})
            ),
          }));
        } else {
          setVendorProductOptions((prev) => ({
            ...prev,
            [vendorId]: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching vendor products:", error);
        setVendorProductOptions((prev) => ({
          ...prev,
          [vendorId]: [],
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    // Handle date validation for manual typing
    if (name === "start_date" || name === "end_date") {
      const today = new Date().toISOString().split("T")[0];

      if (name === "start_date") {
        // Start date cannot be before today
        if (value && value < today) {
          setShowDateWarning(true);
          setTimeout(() => setShowDateWarning(false), 3000);
          return; // Don't update the form
        }
      } else if (name === "end_date") {
        // End date cannot be before start date
        if (value && form.start_date && value < form.start_date) {
          setShowDateWarning(true);
          setTimeout(() => setShowDateWarning(false), 3000);
          return; // Don't update the form
        }
      }

      // Clear warning if date is valid
      setShowDateWarning(false);
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

 const handleImageChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    toast.error("Please select a valid image file");
    return;
  }

  // open crop modal with selected image
  const reader = new FileReader();
  reader.onloadend = () => {
    setRawImageSrc(reader.result);
    setCropOpen(true);            
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };
  reader.readAsDataURL(file);
};

  // Example handler for vendorProducts (customize as needed)
  const handleVendorProductAdd = (vendorId, productId) => {
    debugger;
    setVendorProducts((prev) => [
      ...prev,
      { vendor_id: vendorId, product_id: productId },
    ]);
  };

  const { mutate: addOffer, isLoading: addOfferLoading } = useAddOffer();
  const { mutate: updateOffer, isLoading: updateOfferLoading } =
    useUpdateOffer(id);
  const { uploadImage, uploadStatus } = useFileUploader();

  // Local submitting flag to prevent double submits immediately on click
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoading, SetIsLoading] = useState(false);

    // ✅ API BASE (same style you already use)
  const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

  // ✅ cache all offers (frontend-only check)
  const [offersCache, setOffersCache] = useState([]);

  // ✅ load offers once (only to detect duplicates for single offers)
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/offers`)
      .then((res) => {
        const payload = res?.data?.data || res?.data;
        const list = Array.isArray(payload) ? payload : payload?.data || [];
        setOffersCache(Array.isArray(list) ? list : []);
      })
      .catch(() => setOffersCache([]));
  }, []);

  // ✅ find active, not-expired SINGLE offer for a product
const findActiveSingleOfferForProductVendor = (productId, vendorId) => {
  if (!productId || !vendorId) return null;

  const today = new Date().toISOString().split("T")[0];

  return (offersCache || []).find((o) => {
    const isSingle = String(o?.offer_type) === "single";
    const active = o?.is_active === true || o?.is_active === 1;
    const notExpired = !o?.end_date || String(o.end_date) >= today;

    // ✅ current editing offer ignore
    const notSameOffer = !id || String(o?.id) !== String(id);

    const hasPair =
      Array.isArray(o?.single_product_offers) &&
      o.single_product_offers.some(
        (x) =>
          String(x?.product_id) === String(productId) &&
          String(x?.vendor_id) === String(vendorId)
      );

    return isSingle && active && notExpired && notSameOffer && hasPair;
  });
};

const isPairBlocked = (productId, vendorId) =>
  !!findActiveSingleOfferForProductVendor(productId, vendorId);
const isProductBlocked = (productId) => {
  return !!findActiveSingleOfferForProduct(productId);
};
  // ✅ SweetAlert popup: OK / View / Edit
// ✅ SweetAlert popup: OK / View / Edit
const showExistingOfferPopup = async (offer, focusProductId) => {
  if (!offer?.id) return;

  const result = await Swal.fire({
    icon: "warning",
    title: "Offer already exists",
    html: `This product already has an <b>active</b> offer: <b>${offer?.name || "Offer"}</b>`,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: "OK",
    denyButtonText: "View",
    cancelButtonText: "Edit",
    reverseButtons: true,
  });

  // OK -> just close (do nothing)
  if (result.isConfirmed) return;

  // View -> /offers
  if (result.isDenied) {
    navigate("/offers");
    return;
  }

  // Edit -> /addoffers/<offer_id> (✅ pass focusProductId)
  if (result.dismiss === Swal.DismissReason.cancel) {
    navigate(`/addoffers/${offer.id}`, {
      state: { focusProductId: String(focusProductId || "") },
    });
  }
};

  // ✅ wrapper: check + popup
const checkExistingSingleOfferAndAlert = async (productId) => {
  
  if (!id) {
  const existing = findActiveSingleOfferForProduct(currentProduct);
  if (existing) {
    await showExistingOfferPopup(existing, currentProduct);
    return;
  }
}
  return false; // ok
};

  // Combined loading state
  const isProcessing =
    isLoading ||
    isSubmitting ||
    addOfferLoading ||
    uploadStatus === "uploading";

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Show loading state
    if (isProcessing) return;
    setIsSubmitting(true);

    let imagePath = "";
    // Step 1: Upload image if present
    if (image && image instanceof File) {
      imagePath = usePath(await uploadImage(image, "offers", image.name));

      if (!imagePath) {
        toast.error("Image upload failed!");
        setIsSubmitting(false);
        return;
      }
    }

    // Step 2: Prepare offer data
    let data;
    if (form.offer_type === "combo") {
      if (
        combos.length === 0 ||
        combos.some((combo) => combo.products.length < 2 || !combo.vendor_id)
      ) {
        toast.error(
          "Each combo must have at least 2 products and a selected vendor."
        );
        setIsSubmitting(false);
        return;
      }
      data = {
        ...form,
        image_path: imagePath || form.image_path || "", // Keep existing image if no new one
        combos: combos.map((combo) => ({
          products: combo.products,
          vendor_id: combo.vendor_id,
        })),
        is_active: form.is_active ? 1 : 0,
      };
    } else {
      if (singleOfferList.length < 1) {
        toast.error("Please add at least one product-vendor pair.");
        setIsSubmitting(false);
        return;
      }
      data = {
        ...form,
        image_path: imagePath || form.image_path || "", // Keep existing image if no new one
        single_offer_list: singleOfferList,
        is_active: form.is_active ? 1 : 0,
      };
    }

    // Validate required fields
    if (!data.name || !data.value || !data.start_date || !data.end_date) {
      toast.error("Please fill all required fields");
      setIsSubmitting(false);
      return;
    }

    // Ensure dates are in correct format
    data.start_date = new Date(data.start_date).toISOString().split("T")[0];
    data.end_date = new Date(data.end_date).toISOString().split("T")[0];

    // Step 3: Send offer data
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (Array.isArray(data[key]) || typeof data[key] === "object") {
        formData.append(key, JSON.stringify(data[key]));
      } else if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key]);
      }
    });

    // ✅ ADD THIS HERE
if (id) formData.append("_method", "PUT");

    // Only support creation; editing is disabled
    console.log("Creating new offer");

    if (id) {
      updateOffer(formData, {
        onSuccess: () => {
          toast.success("Offer added successfully!");
          navigate("/offers", {
            state: {
              reload: true,
            },
          });
        },
        onError: (error) => {
          console.error("Offer creation error:", error);
          if (error?.response?.data?.errors) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((field) => {
              toast.error(`${field}: ${errors[field][0]}`);
            });
          } else if (error?.response?.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Error adding offer");
          }
        },
        onSettled: () => setIsSubmitting(false),
      });
    } else {
      addOffer(formData, {
        onSuccess: () => {
          toast.success("Offer added successfully!");
          navigate("/offers", {
            state: {
              reload: true,
            },
          });
        },
        onError: (error) => {
          console.error("Offer creation error:", error);
          if (error?.response?.data?.errors) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((field) => {
              toast.error(`${field}: ${errors[field][0]}`);
            });
          } else if (error?.response?.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Error adding offer");
          }
        },
        onSettled: () => setIsSubmitting(false),
      });
    }
  };

  // When selectedSingleProducts changes, filter vendors
  useEffect(() => {
    if (form.offer_type !== "single") return;
    if (selectedSingleProducts.length === 0) {
      setFilteredVendors([]);
      return;
    }
    const availableVendors = vendors.filter(
  (vendor) =>
    (vendor.products || []).some(
      (p) => String(p.product_id) === String(currentProduct)
    ) &&
    (vendor.user?.name || `Vendor #${vendor.vendor_id}`)
      .toLowerCase()
      .includes(vendorSearch.toLowerCase())
);
    // For each vendor, check if they have at least one selected product
    const filtered = vendors.filter((vendor) =>
      (vendor.products || []).some((p) =>
        selectedSingleProducts.includes(p.product_id)
      )
    );
    setFilteredVendors(filtered);
  }, [selectedSingleProducts, vendors, form.offer_type]);

  // Handler for product checkbox in single offer
  const handleSingleProductCheckbox = (productId) => {
    setSelectedSingleProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
    // Optionally, clear vendors that no longer match any selected product
    setSelectedSingleVendors((prev) =>
      prev.filter((vendorId) => {
        const vendor = vendors.find(
          (v) => String(v.vendor_id) === String(vendorId)
        );
        return (
          vendor &&
          (vendor.products || []).some(
            (p) =>
              selectedSingleProducts.includes(p.product_id) ||
              p.product_id === productId
          )
        );
      })
    );
  };

  // Handler for vendor checkbox in single offer
  const handleSingleVendorCheckbox = (vendorId) => {
    setSelectedSingleVendors((prev) =>
      prev.includes(vendorId)
        ? prev.filter((id) => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  // Custom filter and sort for react-select: prioritize startsWith, then includes, both alphabetically
  function getSortedProductOptions(
    products,
    selectedList,
    search,
    editId = null
  ) {
    const lowerSearch = search.trim().toLowerCase();
    let filtered = products.filter((product) => {
      // Exclude already selected (except editId)
      if (
        selectedList.some(
          (item) =>
            String(item.product_id) === String(product.product_id) &&
            String(item.product_id) !== String(editId)
        )
      )
        return false;
      // If search is empty, include all
      if (!lowerSearch) return true;
      // Only include if matches search
      return product.product_name.toLowerCase().includes(lowerSearch);
    });
    if (!lowerSearch) {
      // If no search, keep original order
      return filtered.map((product) => ({
        value: product.product_id,
        label: product.product_name,
      }));
    }
    // Split into startsWith and includes
    const startsWith = filtered
      .filter((product) =>
        product.product_name.toLowerCase().startsWith(lowerSearch)
      )
      .sort((a, b) => a.product_name.localeCompare(b.product_name));
    const includes = filtered
      .filter(
        (product) => !product.product_name.toLowerCase().startsWith(lowerSearch)
      )
      .sort((a, b) => a.product_name.localeCompare(b.product_name));
    return [...startsWith, ...includes].map((product) => ({
      value: product.product_id,
      label: product.product_name,
    }));
  }
const onCropComplete = (_, croppedPixels) => {
  setCroppedAreaPixels(croppedPixels);
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });

const getCroppedBlob = async (imageSrc, cropPixels) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
};

const handleCropSave = async () => {
  if (!rawImageSrc || !croppedAreaPixels) return;

  const blob = await getCroppedBlob(rawImageSrc, croppedAreaPixels);
  const file = new File([blob], `offer_${Date.now()}.jpg`, { type: "image/jpeg" });

  setImage(file);

  // ✅ preview
  const previewUrl = URL.createObjectURL(file);
  setImagePreview(previewUrl);

  setCropOpen(false);
};

const handleCropCancel = () => {
  setCropOpen(false);
  setRawImageSrc(null);
};
  // Helper for vendor options for current product
  function getVendorOptions(vendors, currentProduct) {
    return vendors
      .filter((vendor) =>
        (vendor.products || []).some(
          (p) => String(p.product_id) === String(currentProduct)
        )
      )
      .map((vendor) => ({
        value: vendor.vendor_id,
        label: vendor.user?.name || `Vendor #${vendor.vendor_id}`,
      }));
  }

  // For Add to List form, filter out already-selected products from options
  const availableProductOptions = products
    .filter(
      (product) =>
        !singleOfferList.some(
          (item) => String(item.product_id) === String(product.product_id)
        )
    )
    .map((product) => ({
      value: product.product_id,
      label: product.product_name,
    }));
  // For Edit form, allow the currently edited product
  const availableEditProductOptions = products
    .filter(
      (product) =>
        !singleOfferList.some(
          (item) =>
            String(item.product_id) === String(product.product_id) &&
            String(item.product_id) !== String(editProductId)
        )
    )
    .map((product) => ({
      value: product.product_id,
      label: product.product_name,
    }));

  // Add state for react-select input value
  const [productInput, setProductInput] = useState("");
  // Reset search input when switching between add/edit or after selecting
  useEffect(() => {
    setProductInput("");
  }, [editProductId, form.offer_type]);

  // Add state for vendor search
  const [vendorSearch, setVendorSearch] = useState("");

  // Effect to clear end date if it becomes invalid when start date changes
  useEffect(() => {
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      setForm((prev) => ({ ...prev, end_date: "" }));
    }
  }, [form.start_date]);

  return (
    <div className="full-container">
      <div className={`add-offer-container-no-box ${isProcessing ? "processing" : ""}`}>
        {isProcessing && (
          <div className="form-loading-overlay">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <p>Processing...</p>
            </div>
          </div>
        )}
        <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 24 }}>
          {id ? "Edit Offer" : "Add New Offer"}
        </h2>
      <form onSubmit={handleSubmit}>
        <div className="add-offer-field">
          <label className="add-offer-label">Offer Name</label>
          <input
            name="name"
            placeholder="Offer Name"
            value={form.name}
            onChange={handleChange}
            className="add-offer-input"
            required
          />
        </div>
        <div className="add-offer-field">
          <label className="add-offer-label">Offer type</label>
          <select
            name="offer_type"
            value={form.offer_type}
            onChange={handleChange}
            className="add-offer-input"
          >
            <option value="single">single offer</option>
            <option value="combo">Combo offer</option>
          </select>
        </div>
        <div className="add-offer-field">
          <label className="add-offer-label">Type</label>
          <select
            name="discount_type"
            value={form.discount_type}
            onChange={handleChange}
            className="add-offer-input"
          >
            {/* <option value="flat">Flat</option> */}
            <option value="percentage">Percentage</option>
          </select>
        </div>
        <div className="add-offer-field">
  <label className="add-offer-label">Value</label>

  <div className="value-input-wrapper">
    <input
      type="number"
      name="value"
      placeholder="Value"
      value={form.value}
      min="0"
      max={form.discount_type === "percentage" ? 100 : undefined}
      onChange={handleChange}
      className="add-offer-input"
      required
    />

    {form.discount_type === "percentage" && (
      <span className="percentage-symbol">%</span>
    )}
  </div>
</div>
        <div className="add-offer-row">
          <div className="add-offer-field add-offer-half">
            <label className="add-offer-label">Start Date</label>
            <input
              name="start_date"
              type="date"
              value={form.start_date}
              onChange={handleChange}
              className="add-offer-input"
              min={form.start_date || new Date().toISOString().split("T")[0]} // Prevent selecting past dates
              required
            />
          </div>
          <div className="add-offer-field add-offer-half">
            <label className="add-offer-label">End Date</label>
            <input
              name="end_date"
              type="date"
              value={form.end_date}
              onChange={handleChange}
              className="add-offer-input"
              min={form.start_date || new Date().toISOString().split("T")[0]} // Prevent selecting dates before start date
              required
            />
          </div>
        </div>
        {showDateWarning && (
          <div className="date-warning-message">
            ⚠️ Invalid date selected. Start date cannot be before today, and end
            date cannot be before start date.
          </div>
        )}

        <div className="add-offer-field">
          <label className="add-offer-label">Offer Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="add-offer-input"
            required={!imagePreview}
          />
          {imagePreview && (
             <div className="offer-banner-preview">
            <SafeImage
              src={imagePreview}
              alt="Offer Preview"
            />
            </div>
          )}
        </div>
        {form.offer_type === "single" && (
          <>
            {/* Only show Add to List form if not editing */}
            {editProductId === null && (
              <>
                <div className="add-offer-field">
                  <label className="add-offer-label">Select Product</label>
                  <Select
                    className="add-offer-input"
                    options={getSortedProductOptions(
                      products,
                      singleOfferList,
                      productInput
                    )}
                    value={
                      getSortedProductOptions(
                        products,
                        singleOfferList,
                        productInput
                      ).find(
                        (option) =>
                          String(option.value) === String(currentProduct)
                      ) || null
                    }
                  onChange={(option) => {
                  const pid = option ? option.value : "";
                  setCurrentProduct(pid);
                  setCurrentVendors([]); // product change  vendors reset ok
                  setProductInput("");
                  }}
                    onInputChange={(value) => setProductInput(value)}
                    inputValue={productInput}
                    placeholder="Select Product"
                    isClearable
                  />
                </div>
                {currentProduct && (
                  <div className="add-offer-field">
                    <label className="add-offer-label">Select Vendors</label>
                    <input
                      type="text"
                      className="add-offer-input"
                      placeholder="Search vendors..."
                      value={vendorSearch}
                      onChange={(e) => setVendorSearch(e.target.value)}
                      style={{ marginBottom: 8 }}
                    />
                  {(() => {
                    const availableVendors = vendors.filter(
                      (vendor) =>
                        (vendor.products || []).some(
                          (p) => String(p.product_id) === String(currentProduct)
                        ) &&
                        (vendor.user?.name || `Vendor #${vendor.vendor_id}`)
                          .toLowerCase()
                          .includes(vendorSearch.toLowerCase())
                    );

                    return (
                      <button
                        type="button"
                        className="select-all-btn"
                        onClick={() => {
                          const allIds = availableVendors.map((v) =>
                            String(v.vendor_id)
                          );

                          const allSelected =
                            allIds.length > 0 &&
                            allIds.every((id) =>
                              currentVendors.map(String).includes(id)
                            );

                          if (allSelected) {
                            setCurrentVendors([]);
                          } else {
                            setCurrentVendors(allIds);
                          }
                        }}
                        style={{ marginBottom: 10 }}
                      >
                        {availableVendors.length > 0 &&
                        availableVendors.every((v) =>
                          currentVendors
                            .map(String)
                            .includes(String(v.vendor_id))
                        )
                          ? "Unselect All"
                          : "Select All"}
                      </button>
                    );
                    })()}
                    <div className="add-offer-products-list">
                      {vendors
                        // Only show vendors who have the selected product (AI change)
                        .filter(
                          (vendor) =>
                            (vendor.products || []).some(
                              (p) =>
                                String(p.product_id) === String(currentProduct)
                            ) &&
                            (vendor.user?.name || `Vendor #${vendor.vendor_id}`)
                              .toLowerCase()
                              .includes(vendorSearch.toLowerCase())
                        )
                        .map((vendor) => (
                          <label
                            key={`vendor-${vendor.vendor_id}-${currentProduct}`}
                            className="add-offer-product-item"
                          >
                            <input
                              type="checkbox"
                              checked={currentVendors.some((vid) => String(vid) === String(vendor.vendor_id))}
                                onChange={(e) => {
                                  const vid = String(vendor.vendor_id);
                                  if (e.target.checked) {
                                    setCurrentVendors((prev) => {
                                      if (prev.map(String).includes(vid)) return prev;
                                      return [...prev, vid];
                                    });
                                  } else {
                                    setCurrentVendors((prev) => prev.filter((id) => String(id) !== vid));
                                  }
                                }}
                            />
                            <span>
                              {vendor.user?.name ||
                                `Vendor #${vendor.vendor_id}`}
                            </span>
                          </label>
                        ))}
                    </div>
                    <button
                      type="button"
                      className="add-offer-button"
                      onClick={handleAddSingleOfferItem}
                      disabled={!currentProduct || currentVendors.length === 0}
                    >
                      Add to List
                    </button>
                  </div>
                )}
              </>
            )}
            {singleOfferList.length > 0 && (
              <div className="add-offer-field">
                <label className="add-offer-label">
                  Selected Product-Vendor Pairs
                </label>
                <table className="offer-pair-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Vendors</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {singleOfferList.map((item, index) =>
                      String(editProductId) === String(item.product_id) ? (
                        <tr key={`edit-${item.product_id}-${index}`}>
                          <td>
                            <Select
                              className="add-offer-input"
                              options={getSortedProductOptions(
                                products,
                                singleOfferList,
                                productInput,
                                editProductId
                              )}
                              value={
                                getSortedProductOptions(
                                  products,
                                  singleOfferList,
                                  productInput,
                                  editProductId
                                ).find(
                                  (option) =>
                                    String(option.value) ===
                                    String(currentProduct)
                                ) || null
                              }
                            onChange={(option) => {
                            const pid = option ? option.value : "";
                            setCurrentProduct(pid);
                            setCurrentVendors([]);
                            setProductInput("");
                          }}
                              onInputChange={(value) => setProductInput(value)}
                              inputValue={productInput}
                              placeholder="Select Product"
                              isClearable
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="add-offer-input"
                              placeholder="Search vendors..."
                              value={vendorSearch}
                              onChange={(e) => setVendorSearch(e.target.value)}
                              style={{ marginBottom: 8 }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              {vendors
                                .filter(
                                  (vendor) =>
                                    (vendor.products || []).some(
                                      (p) =>
                                        String(p.product_id) ===
                                        String(currentProduct)
                                    ) &&
                                    (
                                      vendor.user?.name ||
                                      `Vendor #${vendor.vendor_id}`
                                    )
                                      .toLowerCase()
                                      .includes(vendorSearch.toLowerCase())
                                )
                                .map((vendor) => (
                                  <label
                                    key={`vendor-${vendor.vendor_id}-${currentProduct}`}
                                    className="add-offer-product-item"
                                    style={{ marginBottom: 0 }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={currentVendors.some(
  (vid) => String(vid) === String(vendor.vendor_id)
)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setCurrentVendors((prev) => {
  const vid = String(vendor.vendor_id);
  if (prev.map(String).includes(vid)) return prev; // prevent duplicate
  return [...prev, vid];
});
                                        } else {
                                          setCurrentVendors((prev) =>
                                            prev.filter(
                                              (id) => String(id) !== String(vendor.vendor_id)
                                            )
                                          );
                                        }
                                      }}
                                    />
                                    <span>
                                      {vendor.user?.name ||
                                        `Vendor #${vendor.vendor_id}`}
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="save-offer-pair-btn"
                              onClick={handleSaveEditSingleOfferItem}
                              disabled={
                                !currentProduct || currentVendors.length === 0
                              }
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="cancel-offer-pair-btn"
                              onClick={handleCancelEditSingleOfferItem}
                              style={{ marginLeft: 8 }}
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={`view-${item.product_id}-${index}`}>
                          <td>
                            {
                              products.find(
                                (p) =>
                                  String(p.product_id) ===
                                  String(item.product_id)
                              )?.product_name
                            }
                          </td>
                          <td>
                            {item.vendor_ids.map((id, vendorIndex) => (
                              <span
                                key={`vendor-badge-${id}-${vendorIndex}`}
                                className="vendor-badge"
                              >
                                {
                                  vendors.find((v) => String(v.vendor_id) === String(id))?.user?.name
                                }
                              </span>
                            ))}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="edit-offer-pair-btn"
                              onClick={() =>
                                handleEditSingleOfferItem(item.product_id)
                              }
                              style={{ marginRight: 8 }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="remove-offer-pair-btn"
                              onClick={() =>
                                handleRemoveSingleOfferItem(item.product_id)
                              }
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {form.offer_type === "combo" && (
          <div className="add-offer-field">
            <label className="add-offer-label">
              Create Vendor Combos (select 2+ products from each vendor)
            </label>
            {combos.map((combo, idx) => {
              const vendor = vendors.find(
                (v) => String(v.vendor_id) === String(combo.vendor_id)
              );
              const vendorName = vendor ? vendor.user?.name : "Select Vendor";

              return (
                <div
                  key={`combo-${idx}-${combo.vendor_id || "empty"}`}
                  className="vendor-combo-item"
                >
                  <div className="vendor-combo-header">
                    <div className="vendor-combo-title">
                      <span
                        className={`vendor-name${
                          combo.vendor_id ? " selected" : ""
                        }`}
                      >
                        {vendorName}
                      </span>
                      {combo.products.length > 0 && (
                        <span className="product-count">
                          ({combo.products.length} products)
                        </span>
                      )}
                    </div>
                    <div className="vendor-combo-actions">
                      <button
                        type="button"
                        className="vendor-combo-btn"
                        onClick={() => openComboPopup(idx)}
                      >
                        {combo.products.length > 0
                          ? "Edit Products"
                          : "Select Vendors"}
                      </button>
                      {combos.length > 1 && (
                        <button
                          type="button"
                          className="vendor-combo-remove-btn"
                          onClick={() => removeCombo(idx)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {combo.products.length > 0 ? (
                    <div className="vendor-products-display">
                      <div className="vendor-products-list">
                        {(vendorProductOptions[combo.vendor_id] || [])
                          .filter((product) =>
                            combo.products.includes(product.product_id)
                          )
                          .map((product) => (
                            <span
                              key={`product-${product.product_id}-${idx}`}
                              className="vendor-product-tag"
                            >
                              {product.product_name}
                            </span>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="vendor-combo-empty">
                      <span className="vendor-combo-empty-text">
                        No products selected yet
                      </span>
                    </div>
                  )}

                  {combo.products.length > 0 && combo.products.length < 2 && (
                    <div className="vendor-combo-warning">
                      ⚠️ Need at least 2 products for this combo
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={addCombo}
              className="add-vendor-combo-btn"
            >
              + Add Another Vendor
            </button>
          </div>
        )}
        {/* For single offers, keep the existing vendorProducts checkbox UI */}
        {form.offer_type === "single" && form.vendor_id && (
          <div className="add-offer-field">
            <label className="add-offer-label">
              Select Products for Single Offer
            </label>
            <div className="add-offer-products-list">
              {(vendorProductOptions[form.vendor_id] || []).map((product) => (
                <label
                  key={`single-product-${product.product_id}-${form.vendor_id}`}
                  className="add-offer-product-item"
                >
                  <input
                    type="checkbox"
                    checked={vendorProducts.some(
                      (vp) =>
                        vp.vendor_id === form.vendor_id &&
                        vp.product_id === product.product_id
                    )}
                    onChange={() =>
                      handleVendorProductCheckbox(
                        form.vendor_id,
                        product.product_id
                      )
                    }
                  />
                  <span>{product.product_name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {/* Vendor-Product mapping UI (optional, for vendor_products) */}

        {/* Implement your own UI for adding vendor-product pairs, e.g. dropdowns or a table */}
        <label
          htmlFor="is_active"
          className="add-offer-checkbox-label"
          style={{ marginLeft: "12px" }}
        >
          Active Offer
        </label>

        <div className="add-offer-checkbox-row">
          <label className="switch">
            <input
              name="is_active"
              type="checkbox"
              checked={form.is_active}
              onChange={handleChange}
              id="is_active"
            />
            <span className="slider round"></span>
          </label>
        </div>
        <div className="add-offer-button-row">
          <button
            type="submit"
            className="add-offer-button"
            disabled={isProcessing}
            style={{
              opacity: isProcessing ? 0.7 : 1,
              cursor: isProcessing ? "not-allowed" : "pointer",
              position: "relative",
            }}
          >
            {isProcessing ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  className="loading-spinner"
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "8px",
                  }}
                ></div>
                {id ? "Updating..." : "Adding..."}
              </span>
            ) : id ? (
              "Update Offer"
            ) : (
              "Add Offer"
            )}
          </button>
          <button
            type="button"
            className="add-offer-cancel-button"
            style={{
              marginLeft: 12,
              background: "#bbb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 20px",
              fontWeight: 600,
              cursor: isProcessing ? "not-allowed" : "pointer",
              opacity: isProcessing ? 0.7 : 1,
            }}
            onClick={() => navigate("/offers")}
            disabled={isProcessing}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Vendor Selection Popup */}
      <VendorSelectionPopup
        isOpen={comboPopup.isOpen}
        combos={combos}
        onClose={closeComboPopup}
        selectedProducts={comboPopup.selectedProducts}
        availableProducts={vendorProductOptions[form.vendor_id] || []}
        onConfirm={handleComboConfirm}
        comboIndex={comboPopup.comboIndex}
        vendors={vendors}
        vendorProductOptions={vendorProductOptions}
        onVendorChange={fetchVendorProducts}
        initialVendorId={comboPopup.vendor_id}
        isProductBlocked={form.offer_type === "single" ? isProductBlocked : undefined}
      />

      {/*ADD CROP MODAL RIGHT HERE (BEFORE CLOSING DIVS)*/}
      {cropOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h3>Crop Offer Image</h3>
              <button type="button" onClick={handleCropCancel} className="crop-close">
                ✕
              </button>
            </div>

            <div className="crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1600 / 405}   
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="crop-controls">
              <label>Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>

            <div className="crop-actions">
              <button type="button" onClick={handleCropCancel} className="crop-btn cancel">
                Cancel
              </button>
              <button type="button" onClick={handleCropSave} className="crop-btn save">
                Save Crop
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default AddOffer;
